// Swipeable character deck for sections/fp-hero.liquid.
// Cards are positioned only by data-pos (0 top, 1 and 2 behind, -1 hidden deeper); fp-hero.css does the
// transforms. The order is circular: the top card goes to the back, prev brings the last one forward.
// The quote in the copy column ([data-fp-quote][data-index]) follows the top card.

const DRAG_THRESHOLD = 80; // px past which a release flies the card off
const FLING_VELOCITY = 0.5; // px/ms
const CLICK_SLOP = 8; // px; less movement than this is a click, not a drag
const LEAVE_MS = 320;

class FpHeroDeck extends HTMLElement {
  connectedCallback() {
    this.cards = [...this.querySelectorAll('[data-fp-card]')];
    this.count = this.cards.length;
    if (!this.count) return;

    const section = this.closest('section') || document;
    this.quotes = [...section.querySelectorAll('[data-fp-quote]')];
    this.dots = [...this.querySelectorAll('[data-fp-go]')];
    this.live = this.querySelector('[data-fp-live]');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.active = 0;
    this.busy = false;
    this.drag = null;
    this.suppressClick = false;

    this.querySelector('[data-fp-prev]')?.addEventListener('click', () => this.advance(-1));
    this.querySelector('[data-fp-next]')?.addEventListener('click', () => this.advance(1));
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.fpGo))));

    this.addEventListener('keydown', (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.key === 'ArrowLeft') this.advance(-1);
      else if (event.key === 'ArrowRight') this.advance(1);
      else return;
      event.preventDefault();
    });

    this.addEventListener('pointerdown', (event) => this.onDown(event));
    this.addEventListener('pointermove', (event) => this.onMove(event));
    this.addEventListener('pointerup', (event) => this.onUp(event));
    this.addEventListener('pointercancel', (event) => this.onUp(event, true));
    this.addEventListener('dragstart', (event) => event.preventDefault());
    // A real drag must not also count as a click on whatever is under the pointer.
    this.addEventListener(
      'click',
      (event) => {
        if (!this.suppressClick) return;
        this.suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    // Theme editor: selecting a card block brings it to the top.
    this.onBlockSelect = (event) => {
      const card = event.target.closest?.('[data-fp-card]');
      if (card && this.contains(card)) this.go(Number(card.dataset.index), false);
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    this.render(false);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  get reduced() {
    return this.motion.matches;
  }

  get topCard() {
    return this.cards[this.active];
  }

  /**
   * Step through the deck. dir 1 sends the top card off to the back, -1 brings the last card forward.
   * Kept as the single entry point so an autoplay timer can call it later.
   */
  advance(dir = 1, side = 'left') {
    if (this.busy || this.count < 2) return;
    if (dir > 0) this.flyOff(this.topCard, side);
    else this.go(this.active - 1);
  }

  go(index, announce = true) {
    if (this.busy && announce) return;
    this.active = ((index % this.count) + this.count) % this.count;
    this.render(announce);
  }

  flyOff(card, side) {
    if (this.reduced) {
      this.go(this.active + 1);
      return;
    }
    this.busy = true;
    card.classList.add(`is-leaving-${side}`);
    window.setTimeout(() => {
      card.classList.remove('is-leaving-left', 'is-leaving-right');
      this.busy = false;
      this.go(this.active + 1);
    }, LEAVE_MS);
  }

  render(announce) {
    const focusWasOnCard = this.cards.some((card) => card.contains(document.activeElement));

    this.cards.forEach((card, i) => {
      const rel = (i - this.active + this.count) % this.count;
      const pos = rel <= 2 ? rel : -1;
      card.dataset.pos = String(pos);
      card.inert = pos !== 0;
      card.style.removeProperty('--fp-drag-x');
      card.style.removeProperty('--fp-drag-rot');
    });

    this.dots.forEach((dot) => {
      if (Number(dot.dataset.fpGo) === this.active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    this.quotes.forEach((quote) => {
      quote.hidden = Number(quote.dataset.index) !== this.active;
    });

    if (focusWasOnCard) this.topCard.querySelector('button, a')?.focus({ preventScroll: true });

    if (announce && this.live) {
      this.live.textContent = `Card ${this.active + 1} of ${this.count}: ${this.topCard.dataset.name || ''}`;
    }
  }

  onDown(event) {
    if (this.busy || this.count < 2) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const card = event.target.closest('[data-fp-card]');
    if (!card || card !== this.topCard) return;
    // Leave the play button (and any future links) to their own clicks.
    if (event.target.closest('button, a, input, select, textarea')) return;

    try {
      card.setPointerCapture(event.pointerId);
    } catch {
      // Pointer already gone (or synthetic); the drag still works while the pointer stays over the card.
    }
    card.classList.add('is-dragging');
    this.drag = {
      card,
      id: event.pointerId,
      x0: event.clientX,
      dx: 0,
      lastX: event.clientX,
      lastT: event.timeStamp,
      v: 0,
      moved: false,
    };
  }

  onMove(event) {
    const d = this.drag;
    if (!d || event.pointerId !== d.id) return;
    d.dx = event.clientX - d.x0;
    if (!d.moved && Math.abs(d.dx) >= CLICK_SLOP) d.moved = true;

    const dt = event.timeStamp - d.lastT;
    if (dt > 0) {
      const instant = (event.clientX - d.lastX) / dt;
      d.v = 0.8 * instant + 0.2 * d.v;
      d.lastX = event.clientX;
      d.lastT = event.timeStamp;
    }

    d.card.style.setProperty('--fp-drag-x', `${d.dx}px`);
    d.card.style.setProperty('--fp-drag-rot', `${d.dx / 12}deg`);
  }

  onUp(event, cancelled = false) {
    const d = this.drag;
    if (!d || event.pointerId !== d.id) return;
    this.drag = null;
    if (d.card.hasPointerCapture?.(d.id)) d.card.releasePointerCapture(d.id);
    d.card.classList.remove('is-dragging');

    if (d.moved) {
      this.suppressClick = true;
      window.setTimeout(() => (this.suppressClick = false), 0);
    }

    // A pause before letting go means no fling.
    const velocity = event.timeStamp - d.lastT > 100 ? 0 : d.v;
    const flung = Math.abs(velocity) > FLING_VELOCITY;
    if (!cancelled && d.moved && (Math.abs(d.dx) > DRAG_THRESHOLD || flung)) {
      const dir = d.dx !== 0 ? Math.sign(d.dx) : Math.sign(velocity);
      this.advance(1, dir < 0 ? 'left' : 'right');
      return;
    }

    // Spring back (the CSS transition does the animating).
    d.card.style.removeProperty('--fp-drag-x');
    d.card.style.removeProperty('--fp-drag-rot');
  }
}

if (!customElements.get('fp-hero-deck')) customElements.define('fp-hero-deck', FpHeroDeck);
